<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { Control, setupControl } from "./controls";
  import FormField from "./FormField.svelte";
  import { Validator, type ValidatorMessages } from "./validators";
  import type { ControlValidator } from "./validators";

  type TControl = $$Generic<Control<number>>;
  const dispatch = createEventDispatcher<{ change: TControl; decrement: number; increment: number }>();

  // either set name and validators
  export let name: string = null;
  export let min: number = undefined;
  export let max: number = undefined;
  export let step: number = 1;
  export let required: boolean = false;
  export let validators: ControlValidator[] = [];
  export let value: number = undefined;
  // ... or bind a control
  export let control: TControl = null;
  export let standalone: boolean = false;
  export let displayName: string = undefined;
  export let errors: ValidatorMessages = undefined;
  export let label: string = undefined;

  const changes = setupControl<TControl>(Control as any, {
    name,
    validators: [...validators, Validator.minmax(min, max), ...(required ? [Validator.required] : [])],
    value: fixValue(value),
    control,
    standalone,
    configure: (ctrl) => {
      if (isNaN(ctrl.value)) ctrl.value = fixValue(0);
    },
  });
  $: ctrl = $changes;

  let oldValue = value;
  $: if (value !== oldValue) {
    oldValue = value;
    ctrl.value = fixValue(value);
  }

  function fixValue(value: number) {
    return Math.max(min || -Infinity, Math.min(value, max || Infinity)) || min || 0;
  }

  function decrement() {
    ctrl.value = +ctrl.value - step;
    if (ctrl.value < min) {
      ctrl.value = min;
    } else dispatch("decrement", ctrl.value);
    ctrl.touch();
  }

  function increment() {
    ctrl.value = +ctrl.value + step;
    if (ctrl.value > max) {
      ctrl.value = max;
    } else dispatch("increment", ctrl.value);
    ctrl.touch();
  }

  function handleKeydown(e: KeyboardEvent, handler: Function) {
    if (e.code == "Space" || e.code == "Enter") handler();
  }
</script>

<FormField bind:control={ctrl} displayName={displayName || label} {errors} {standalone} on:change={(_) => dispatch("change", control)}>
  <div class="number-spinner-control">
    <span>
      <slot>{label}</slot>
    </span>
    <div class="number-spinner" class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty}>
      <button type="button" class="bs3-btn number-spinner-button" disabled={ctrl.value <= min} on:click={decrement} on:keydown={(e) => handleKeydown(e, decrement)}>-</button>
      <div class="number-spinner-value">{+ctrl.value}</div>
      <button type="button" class="bs3-btn number-spinner-button" disabled={ctrl.value >= max} on:click={increment} on:keydown={(e) => handleKeydown(e, increment)}>+</button>
    </div>
  </div>
</FormField>

<style lang="scss" global>
  .number-spinner-control {
    display: flex;
    justify-content: space-between;
    align-items: center;
    span {
      margin-right: 1em;
    }
  }

  .number-spinner {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    --size: 34px;

    &.input-error {
      .number-spinner-value {
        // border-color: #f5c6cb;
        // background-color: #f8d7da;
        color: red;
      }
    }
  }

  .number-spinner-value {
    padding: 0 0.5em;
    min-width: 2ch;
    box-sizing: content-box;
    text-align: center;
  }

  .number-spinner-button {
    display: inline-flex;
    padding: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: #000000;
    background-color: #fff;
    background-clip: padding-box;
    border: 2px solid #d9d9d9;
    border-radius: 999px;
    width: var(--size);
    height: var(--size);
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;
    box-sizing: border-box;
    cursor: pointer;
    user-select: none;

    &:disabled {
      background-color: #d9d9d9;
    }

    &:focus {
      // border-color: #86b7fe;
      // outline: 0;
      // box-shadow: 0 0 0 0.25rem rgb(13 110 253 / 25%);
      border-color: #000;
    }
  }
</style>
